import { GridGroupingColDefOverride, GridValidRowModel } from '@mui/x-data-grid-premium';
import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { NoteInput } from 'sr-types/lib/note/v1/graphql';
import { KeyValue, Location, Production, Resource } from 'sr-types/lib/production/v1/graphql';
import { LineItem, Reference, Shortlist } from 'sr-types/lib/shortlist/v1/graphql';
import type { KeyCreditsAIClickHandler } from '../common/keyCredits';
import { ADMINISTRATION, PROFILES } from '../common/nav/apps';
import { EMPTY_DATERANGE } from '../lab/form/LabForms';
import { getStringFromDateRange } from '../production/helpers/activityUtils';
import { InquiryStatus, InquiryStatusEnum } from './types';

export interface ShortlistLineItemsProps {
    defaultShortlistId?: string;
    shortlistRoles?: string[];
    shortlistCategories?: string[];
    isTypeCrewOrCast: boolean;
    resourceData?: Resource;
    productionData?: Production;
    resourceNote?: NoteInput;
    handleItemClick?: Dispatch<SetStateAction<any>>;
    setIsMarketplaceOpen?: Dispatch<SetStateAction<boolean | string>>;
    onOpenKeyCreditsAI?: KeyCreditsAIClickHandler;
    onCloseKeyCreditsAI?: () => void;
    isAIEnabled?: boolean;
    loadingShortListLineItems?: boolean;
    shortListLineItems?: LineItemExtended[];
    setShortListLineItems?: Dispatch<SetStateAction<LineItemExtended[]>>;
    isMultiShortlist?: boolean;
}

export type ItemInfo = {
    id?: string;
    ref: Reference;
    isSharedInDirectory?: Boolean;
    label?: string;
    security?: { allowedActions?: string[] };
    professionalRoles?: string[];
};

export type LineItemAdditionalFields = {
    inquiryStatus?: InquiryStatus | string;
    isPerson?: boolean;
    isOrganization?: boolean;
    id?: string | number;
    availability?: InquiryStatus | string;
    itemInfo?: ItemInfo;
    security?: {};
    shortlist?: string;
    resourceShortlistName?: Shortlist;
};

export type LineItemExtended = LineItem & LineItemAdditionalFields;

export type LineItemRows = LineItemExtended & { __reorder__: string; noteExists?: boolean };

type LineItemKeyCredits = LineItem extends { keyCredits?: infer T } ? T : never;

export type LineItemWithKeyCredits = Pick<LineItem, 'identity'> & {
    keyCredits?: LineItemKeyCredits;
};

export const defineShortlistLineItemRows = (
    lineItemList: LineItemExtended[],
    alternateProposeText: string | ReactElement,
    locale
) => {
    return lineItemList.length
        ? lineItemList.map((shortlistLineItem: LineItemExtended, index) => {
              const inquiryStatus = shortlistLineItem?.inquiry?.inquiryStatus;
              const proposedDate = shortlistLineItem?.inquiry?.proposedDate;
              const alternateProposed = `${inquiryStatus} (${!proposedDate?.end ? alternateProposeText + ' ' : ''}${getStringFromDateRange(proposedDate, locale)})`;

              const availability = inquiryStatus
                  ? inquiryStatus === InquiryStatusEnum.alternateProposed && proposedDate
                      ? alternateProposed
                      : inquiryStatus
                  : InquiryStatusEnum.empty;

              return {
                  id: index,
                  ...shortlistLineItem,
                  isPerson: shortlistLineItem.itemReference?.type === 'Person',
                  isOrganization: shortlistLineItem.itemReference?.type === 'Organization',
                  inquiryStatus: shortlistLineItem?.inquiry?.inquiryStatus || InquiryStatusEnum.empty,
                  availability,
                  referenceUrl: shortlistLineItem.referenceUrl,
                  representatives: shortlistLineItem.representatives,
                  __reorder__: shortlistLineItem.name
              };
          })
        : [];
};

export const defineCommonInquiryProductionData = (
    resourceData: Resource,
    productionData: Production,
    productionOptions,
    personalizedMessage: string,
    orgName?: string
) => {
    return {
        resource: { ...resourceData?.reference, label: resourceData.name },
        resourceCategory: resourceData?.resourceCategory,
        locations: resourceData.locations,
        dateRange: resourceData.resourceSchedule?.dateRange || { ...EMPTY_DATERANGE.dateRange },
        production: {
            reference: productionData.reference,
            name: productionData.name,
            headline: '',
            productionType: productionData.productionType
        },
        productionOptions: [
            ...productionOptions,
            ...(productionData
                ? [
                      {
                          id: productionData.name,
                          label: productionData.name
                      }
                  ]
                : [])
        ],
        productionSummary: productionData.about || '',
        personalizedMessage,
        attachments: [],
        senderOrganizationName: orgName
    };
};

export const productionOtherTitlesToOptions = (otherTitles: Array<KeyValue>) => {
    return (
        otherTitles
            ?.filter((item) => item.value)
            .map((title) => {
                return { id: title?.value, label: title?.value, key: title?.key };
            }) || []
    );
};

export const shortlistInviteeCrewFilter = (resLocations: Location[], roles: string | string[]) => {
    const roleValues = Array.isArray(roles) ? roles : roles ? [roles] : [];
    if (resLocations?.length) {
        let resLocalityFilter = null;

        if (resLocations[0].address?.locality) {
            resLocalityFilter = {
                field: 'locality',
                value: {
                    values: resLocations[0].address?.locality
                }
            };
        }

        return {
            ...(roleValues.length > 0 && {
                shouldFilters: [
                    {
                        expressions: [
                            { field: 'jobRoles', value: { values: roleValues } },
                            { field: 'professionalRoles', value: { values: roleValues } }
                        ]
                    }
                ]
            }),
            mustFilters: [
                {
                    expressions: [
                        {
                            field: 'country',
                            value: {
                                values: resLocations[0].address?.countryRegionIso2
                            }
                        },
                        ...(resLocalityFilter ? [resLocalityFilter] : []),
                        { field: 'public', value: { values: ['false'] } }
                    ]
                }
            ]
        };
    } else {
        return {
            shouldFilters: [
                {
                    expressions: [
                        { field: 'jobRoles', value: { values: roles } },
                        { field: 'professionalRoles', value: { values: roles } }
                    ]
                }
            ],
            mustFilters: [
                {
                    expressions: [{ field: 'public', value: { values: ['false'] } }]
                }
            ]
        };
    }
};

export const shortlistInviteeOrgFilter = (resLocations?: Location[], resCategory?: string | string[]) => {
    const hasCategory =
        resCategory && (Array.isArray(resCategory) ? resCategory.length > 0 : resCategory.trim() !== '');
    const hasLocations = Array.isArray(resLocations) && resLocations.length > 0;

    if (!hasCategory && !hasLocations) {
        return undefined;
    }

    const categories = hasCategory ? (Array.isArray(resCategory) ? resCategory : [resCategory]) : [];

    const expressions: any[] = [];

    if (hasCategory) {
        expressions.push({
            field: 'hierarchicalCategories',
            value: { values: categories }
        });
    }

    if (hasLocations) {
        const loc = resLocations![0];
        if (loc.address?.countryRegionIso2) {
            expressions.push({
                field: 'country',
                value: { values: loc.address.countryRegionIso2 }
            });
        }
        if (loc.address?.locality) {
            expressions.push({
                field: 'locality',
                value: { values: loc.address.locality }
            });
        }
    }

    expressions.push(
        { field: 'public', value: { values: ['false'] } },
        { field: 'inactive', value: { values: ['false'] } }
    );

    return {
        mustFilters: [
            {
                expressions
            }
        ]
    };
};

export const RESOURCE_TYPE = {
    CREW: 'Crew',
    CAST: 'Cast'
};

export const changeUrl = (application, isOrganizationDetails, item) => {
    const currentUrl = new URL(window.location.href);
    const queryParams = currentUrl.search;
    const hasId = item.id || item.reference?.id;
    const isAdminOrProfile = application.home === ADMINISTRATION.home || application.home === PROFILES.home;
    const entityRedirect = isAdminOrProfile
        ? isOrganizationDetails
            ? 'organizationunits'
            : 'peopleresources'
        : isOrganizationDetails
          ? 'organizations'
          : 'people';

    const path = `${application.home}/${entityRedirect}/${hasId}/details${queryParams}`;

    window.open(path, '_blank');
};

// Marketplace / talent hub filters

export function buildPersonPrefilters({ role, locations }) {
    const preFilters = [];

    if (role) {
        preFilters.push({ identifier: 'searchAllPersonRoles', value: role });
    }

    if (Array.isArray(locations)) {
        locations.forEach((loc) => {
            const address = loc?.address || {};

            if (address.locality) {
                preFilters.push({ identifier: 'locality', value: address.locality });
            }
            if (address.countryRegionIso2) {
                preFilters.push({ identifier: 'country', value: address.countryRegionIso2 });
            }
        });
    }

    return preFilters;
}

export function buildOrgPrefilters({ category, locations }) {
    const preFilters = [];

    if (category) {
        preFilters.push({ identifier: 'hierarchicalCategories', value: category });
        preFilters.push({ identifier: 'inactive', value: 'false' });
        preFilters.push({ identifier: 'public', value: 'false' });

        if (Array.isArray(locations)) {
            locations.forEach((loc) => {
                const address = loc?.address || {};

                if (address.locality) {
                    preFilters.push({ identifier: 'locality', value: address.locality });
                }

                if (address.countryRegionIso2) {
                    preFilters.push({ identifier: 'country', value: address.countryRegionIso2 });
                }
            });
        }
    }

    return preFilters;
}

export const shortlistGroupingColDef: GridGroupingColDefOverride<GridValidRowModel> = {
    leafField: 'name',
    minWidth: 200
};
